import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const LanguageTranslator = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'zh-CN', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
  ];

  useEffect(() => {
    // Add Google Translate script
    if (!document.getElementById('google-translate-script')) {
      // Define initialization function
      window.googleTranslateElementInit = () => {
        console.log('🔄 googleTranslateElementInit called');
        
        if (window.google && window.google.translate && window.google.translate.TranslateElement) {
          try {
            const element = document.getElementById('google_translate_element');
            if (!element) {
              console.error('❌ google_translate_element container not found');
              return;
            }

            // Initialize Google Translate
            new window.google.translate.TranslateElement(
              {
                pageLanguage: 'en',
                includedLanguages: 'en,fr,es,pt,zh-CN,ar',
                // Force SIMPLE layout to get select dropdown
                layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
                autoDisplay: false,
                // Try to force simple mode
                multilanguagePage: true
              },
              'google_translate_element'
            );
            console.log('✅ Google Translate initialized successfully');
            
            // Poll for widget creation with retry mechanism
            let widgetCheckAttempts = 0;
            const maxWidgetChecks = 20;
            
            const checkWidget = () => {
              const combo = document.querySelector('.goog-te-combo');
              if (combo) {
                // Widget found - no need to log success
              } else if (widgetCheckAttempts < maxWidgetChecks) {
                widgetCheckAttempts++;
                // Only log every 10 attempts to reduce console spam
                if (widgetCheckAttempts === maxWidgetChecks) {
                  console.warn('⚠️ Widget initialization taking longer than expected. May be blocked by ad blocker.');
                }
                setTimeout(checkWidget, 500);
              }
            };
            
            // Start checking after short delay
            setTimeout(checkWidget, 500);
          } catch (error) {
            console.error('❌ Error initializing Google Translate:', error);
          }
        } else {
          console.error('❌ Google Translate API not available');
        }
      };

      // Load the script
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onerror = () => {
        console.error('❌ Failed to load Google Translate script. Check your internet connection.');
        toast.error('Translation service unavailable. Please check your internet connection.', {
          duration: 4000,
          position: 'top-right',
        });
      };
      script.onload = () => {
        console.log('✅ Google Translate script loaded');
      };
      document.head.appendChild(script);
    } else {
      // Script already exists - silently check widget
      setTimeout(() => {
        const combo = document.querySelector('.goog-te-combo');
        if (!combo) {
          // Widget not found - may be blocked, but don't spam console
        }
      }, 500);
    }
  }, []);

  const changeLanguage = (langCode) => {
    setIsDropdownOpen(false);

    if (langCode === 'en') {
      // Restore original content by clearing cookies and reloading
      console.log('🔄 Resetting to English');
      
      // Clear Google Translate cookies
      document.cookie = 'googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC';
      document.cookie = `googtrans=; path=/; domain=${window.location.hostname}; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      
      // Clear hash
      window.location.hash = '';
      
      setCurrentLanguage('en');
      toast.success('Resetting to English... Reloading page...', {
        duration: 1500,
        position: 'top-right',
      });
      
      // Reload page to show original
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
      return;
    }

    // IMMEDIATE check - don't wait, just try to find widget right now
    console.log('🔍 Searching for Google Translate widget...');
    
    // Method 1: Try simple mode (select dropdown)
    const selectElement = document.querySelector('.goog-te-combo');
    
    // Method 2: Try advanced mode (styled link)
    const linkElement = document.querySelector('.goog-te-gadget-simple a, .goog-te-gadget a');
    
    // Method 3: Try to find any translate element
    const anyWidget = document.querySelector('.goog-te-gadget, .skiptranslate');
    
    console.log('📊 Widget detection:');
    console.log('  - Simple mode (.goog-te-combo):', !!selectElement);
    console.log('  - Advanced mode (link):', !!linkElement);
    console.log('  - Any widget (.goog-te-gadget):', !!anyWidget);
    
    if (selectElement) {
      // SIMPLE MODE - Use select dropdown
      console.log('✅ Using simple mode');
      selectElement.value = langCode;
      selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      
      setCurrentLanguage(langCode);
      const langName = languages.find(l => l.code === langCode)?.name || langCode;
      toast.success(`Translating to ${langName}...`, {
        duration: 2000,
        position: 'top-right',
      });
      console.log(`✅ Language changed to ${langName} (simple mode)`);
      return;
    }
    
    if (linkElement) {
      // ADVANCED MODE - Use Google Translate's cookie method
      console.log('✅ Using advanced mode - setting cookie');
      
      // Set Google Translate cookie
      document.cookie = `googtrans=/en/${langCode}; path=/`;
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname}`;
      
      // Also set the hash (backup method)
      window.location.hash = `googtrans(en|${langCode})`;
      
      setCurrentLanguage(langCode);
      const langName = languages.find(l => l.code === langCode)?.name || langCode;
      
      // Reload page to apply translation
      toast.success(`Translating to ${langName}... Reloading page...`, {
        duration: 1500,
        position: 'top-right',
      });
      
      console.log(`✅ Cookie set, reloading for ${langName}`);
      
      // Reload page after short delay
      setTimeout(() => {
        window.location.reload();
      }, 500);
      
      return;
    }
    
    // If neither found, show error immediately
    console.error('❌ No widget found - neither simple nor advanced mode');
    console.error('💡 Container HTML:', document.getElementById('google_translate_element')?.innerHTML || 'Container not found');
    
    toast.error('Translator not ready yet. Please wait a few more seconds and try again.', {
      duration: 4000,
      position: 'top-right',
    });
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Hidden Google Translate Element */}
      <div
        id="google_translate_element"
        style={{ display: 'none' }}
      ></div>

      {/* Custom Language Selector */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.35rem 0.75rem',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: '500',
            color: '#374151',
            transition: 'all 0.2s',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            minHeight: '32px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#6B8E23';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(107, 142, 35, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#E5E7EB';
            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
          }}
        >
          <span style={{ fontSize: '1rem' }}>{getCurrentLanguage().flag}</span>
          <span>{getCurrentLanguage().name}</span>
          <svg
            width="10"
            height="10"
            viewBox="0 0 12 12"
            fill="none"
            style={{
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 0.25rem)',
              right: '0',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              minWidth: '160px',
              overflow: 'hidden'
            }}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: 'none',
                  backgroundColor: currentLanguage === lang.code ? '#F0F4E8' : '#FFFFFF',
                  color: currentLanguage === lang.code ? '#6B8E23' : '#374151',
                  fontSize: '0.75rem',
                  fontWeight: currentLanguage === lang.code ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (currentLanguage !== lang.code) {
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentLanguage !== lang.code) {
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                  }
                }}
              >
                <span style={{ fontSize: '1rem' }}>{lang.flag}</span>
                <span>{lang.name}</span>
                {currentLanguage === lang.code && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    style={{ marginLeft: 'auto' }}
                  >
                    <path
                      d="M13.3333 4L6 11.3333L2.66667 8"
                      stroke="#6B8E23"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {isDropdownOpen && (
        <div
          onClick={() => setIsDropdownOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
        />
      )}
    </div>
  );
};

export default LanguageTranslator;
